import React, { useMemo, useState } from "react";
import { Alert, Collapse, Divider, Drawer, Input, Tabs, Tag, Typography } from "antd";
import {
    CATEGORIAS,
    CLAVES_INCIDENCIA,
    EJEMPLOS,
    FUNCIONES,
    IMPORTES,
    NOTAS_IMPORTES,
    OPERADORES,
    REGLAS_BASICAS,
    SIEMPRE_CERO,
    VARIABLES_CONTEXTO,
} from "./catalogoFormulas";
import {
    Codigo,
    MONO,
    Nombre,
    Renglon,
    Z_INDEX_GUIA,
    anchoDrawerGuia,
} from "../componentesGuia";

const { Text, Paragraph, Title } = Typography;
const { TabPane } = Tabs;
const { Panel } = Collapse;

// ── Pestaña 1: cómo empezar ─────────────────────────────────────────────────
const TabComoEmpezar = () => (
    <>
        <Paragraph>
            Una fórmula es una cuenta que el sistema resuelve para cada empleado cuando calculas la
            prenómina. En lugar de números fijos usas <Text strong>variables</Text>, que son los datos
            de ese empleado y de ese periodo: su salario, los días que se pagan, sus faltas.
        </Paragraph>

        <Codigo>{"DIAS_TRABAJADOS * SALARIO_DIARIO"}</Codigo>
        <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 6 }}>
            Para un empleado con salario de 400 en una quincena de 15 días, eso da 6,000. Para otro con
            salario de 300, da 4,500. La fórmula es la misma; los datos cambian.
        </Paragraph>

        <Divider orientation="left" plain>
            Reglas que conviene tener claras
        </Divider>
        <ul style={{ paddingLeft: 18, fontSize: 13 }}>
            {REGLAS_BASICAS.map((r, i) => (
                <li key={i} style={{ marginBottom: 6 }}>
                    {r}
                </li>
            ))}
        </ul>

        <Alert
            type="warning"
            showIcon
            style={{ marginTop: 4 }}
            message="Un error en la fórmula no da error: da cero"
            description="Si te equivocas en el nombre de una variable, dejas un paréntesis abierto o usas algo que no existe, el concepto simplemente sale en 0 y nadie te avisa. Siempre revisa el resultado en una prenómina de prueba antes de darlo por bueno."
        />

        <Divider orientation="left" plain>
            Operadores
        </Divider>
        {OPERADORES.map((o) => (
            <div key={o.simbolo} style={{ marginBottom: 8 }}>
                <Tag style={MONO}>{o.simbolo}</Tag>
                <Text style={{ fontSize: 12, color: "rgba(0,0,0,0.65)" }}>{o.desc}</Text>
            </div>
        ))}

        <Divider orientation="left" plain>
            Variables que hoy siempre valen cero
        </Divider>
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
            Existen en el sistema pero todavía no se calculan. Si las usas, tu fórmula no va a hacer lo
            que esperas.
        </Paragraph>
        {SIEMPRE_CERO.map((v) => (
            <Renglon key={v.nombre} nombre={v.nombre} desc={v.motivo} />
        ))}
    </>
);

// ── Pestaña 2: importes ─────────────────────────────────────────────────────
const TabImportes = () => (
    <>
        <Paragraph>
            Cada concepto tiene cinco fórmulas. La de <Text strong>Importe Total</Text> es el dinero que
            se paga o se descuenta. Las otras cuatro no suman nada al recibo: sirven para partir ese
            total y decirle al sistema qué parte causa impuestos y qué parte no.
        </Paragraph>
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
            Ejemplo típico: un aguinaldo de 5,000 se paga completo (Importe Total), pero una parte está
            exenta de ISR. Esa separación se hace en Importe 1 y Importe 2, y es de donde el sistema saca
            la base para calcular el ISR.
        </Paragraph>

        {IMPORTES.map((imp) => (
            <div
                key={imp.campo}
                style={{ border: "1px solid #f0f0f0", borderRadius: 4, padding: 12, marginBottom: 10 }}
            >
                <Tag color="blue">{imp.campo}</Tag>
                <Text strong>{imp.titulo}</Text>
                <Paragraph style={{ fontSize: 12, marginTop: 8, marginBottom: 6 }}>{imp.para}</Paragraph>
                <div style={{ fontSize: 12, color: "rgba(0,0,0,0.65)", marginBottom: 4 }}>
                    <Text strong style={{ fontSize: 12 }}>
                        A dónde suma:{" "}
                    </Text>
                    {imp.acumula}
                </div>
                <div style={{ fontSize: 12, color: "rgba(0,0,0,0.65)" }}>
                    <Text strong style={{ fontSize: 12 }}>
                        Qué puedes usar aquí:{" "}
                    </Text>
                    {imp.disponible}
                </div>
            </div>
        ))}

        <Divider orientation="left" plain>
            Cosas que hay que saber
        </Divider>
        <ul style={{ paddingLeft: 18, fontSize: 13 }}>
            {NOTAS_IMPORTES.map((n, i) => (
                <li key={i} style={{ marginBottom: 6 }}>
                    {n}
                </li>
            ))}
        </ul>

        <Divider orientation="left" plain>
            Variables que dependen de dónde escribas
        </Divider>
        {VARIABLES_CONTEXTO.map((v) => (
            <div key={v.nombre} style={{ marginBottom: 10, lineHeight: 1.5 }}>
                <Nombre>{v.nombre}</Nombre>
                <Tag color="geekblue" style={{ fontSize: 11 }}>
                    {v.donde}
                </Tag>
                <div style={{ fontSize: 12, color: "rgba(0,0,0,0.65)" }}>{v.desc}</div>
            </div>
        ))}
    </>
);

// ── Pestaña 3: variables ────────────────────────────────────────────────────
const CATEGORIA_INICIAL = ["asistencia"];

const TabVariables = () => {
    const [busqueda, setBusqueda] = useState("");
    const [abiertas, setAbiertas] = useState(CATEGORIA_INICIAL);

    const filtro = busqueda.trim().toUpperCase();

    const categorias = useMemo(() => {
        if (!filtro) return CATEGORIAS;
        return CATEGORIAS.map((c) => ({
            ...c,
            variables: c.variables.filter(
                (v) => v.nombre.includes(filtro) || v.desc.toUpperCase().includes(filtro)
            ),
        })).filter((c) => c.variables.length > 0);
    }, [filtro]);

    // Al buscar se abren solas las categorías que traen resultados, pero el Collapse
    // queda controlado para que el usuario pueda seguir abriendo y cerrando a mano.
    const buscar = (texto) => {
        setBusqueda(texto);
        const f = texto.trim().toUpperCase();
        if (!f) {
            setAbiertas(CATEGORIA_INICIAL);
            return;
        }
        setAbiertas(
            CATEGORIAS.filter((c) =>
                c.variables.some((v) => v.nombre.includes(f) || v.desc.toUpperCase().includes(f))
            ).map((c) => c.clave)
        );
    };

    const total = CATEGORIAS.reduce((s, c) => s + c.variables.length, 0);
    const encontradas = categorias.reduce((s, c) => s + c.variables.length, 0);

    return (
        <>
            <Input.Search
                allowClear
                placeholder="Buscar una variable (ej: salario, falta, ISR)"
                value={busqueda}
                onChange={(e) => buscar(e.target.value)}
                style={{ marginBottom: 10 }}
            />
            <Paragraph type="secondary" style={{ fontSize: 12 }}>
                {filtro
                    ? `${encontradas} de ${total} variables coinciden con "${busqueda}".`
                    : `${total} variables disponibles. Da clic en cualquier nombre para copiarlo.`}
            </Paragraph>

            {categorias.length === 0 && (
                <Alert type="info" showIcon message="Ninguna variable coincide con esa búsqueda." />
            )}

            <Collapse activeKey={abiertas} onChange={(claves) => setAbiertas(claves)}>
                {categorias.map((c) => (
                    <Panel
                        key={c.clave}
                        header={
                            <span>
                                {c.titulo}{" "}
                                <Tag style={{ marginLeft: 4 }}>{c.variables.length}</Tag>
                                {c.avanzado && (
                                    <Tag color="orange" style={{ fontSize: 11 }}>
                                        avanzado
                                    </Tag>
                                )}
                            </span>
                        }
                    >
                        {c.resumen && (
                            <Paragraph type="secondary" style={{ fontSize: 12 }}>
                                {c.resumen}
                            </Paragraph>
                        )}
                        {c.variables.map((v) => (
                            <Renglon key={v.nombre} nombre={v.nombre} desc={v.desc} />
                        ))}
                    </Panel>
                ))}
            </Collapse>
        </>
    );
};

// ── Pestaña 4: funciones ────────────────────────────────────────────────────
const TabFunciones = () => (
    <>
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
            Estas son todas las funciones que entienden las fórmulas. Cualquier otro nombre de función
            deja el concepto en cero.
        </Paragraph>
        {FUNCIONES.map((f) => (
            <div
                key={f.firma}
                style={{ border: "1px solid #f0f0f0", borderRadius: 4, padding: 12, marginBottom: 10 }}
            >
                <Text strong style={MONO}>
                    {f.firma}
                </Text>
                <Paragraph style={{ fontSize: 12, marginTop: 8, marginBottom: 8 }}>{f.desc}</Paragraph>
                <Codigo>{f.ejemplo}</Codigo>
                {f.nota && (
                    <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
                        {f.nota}
                    </Paragraph>
                )}
            </div>
        ))}

        <Divider orientation="left" plain>
            Claves de incidencia
        </Divider>
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
            Son las que van entre comillas en <Text style={MONO}>INCIDENCIA</Text> y{" "}
            <Text style={MONO}>TIENE_INCIDENCIA</Text>. Esta es la lista que crea el asistente de nómina;
            la lista real de tu empresa está en Panel → Tipos de Incidencias, y si diste de alta claves
            propias también funcionan.
        </Paragraph>
        <div>
            {CLAVES_INCIDENCIA.map((c) => (
                <div key={c.clave} style={{ display: "inline-block", marginRight: 12, marginBottom: 6 }}>
                    <Nombre>{c.clave}</Nombre>
                    <Text style={{ fontSize: 12, color: "rgba(0,0,0,0.65)" }}>{c.nombre}</Text>
                </div>
            ))}
        </div>
    </>
);

// ── Pestaña 5: ejemplos ─────────────────────────────────────────────────────
const TabEjemplos = () => (
    <>
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
            Fórmulas listas para copiar. Da clic en cualquiera para copiarla y pégala en el campo que
            indica la etiqueta.
        </Paragraph>
        {EJEMPLOS.map((e, i) => (
            <div key={i} style={{ marginBottom: 16 }}>
                <Text strong>{e.titulo}</Text>{" "}
                <Tag color="blue" style={{ fontSize: 11 }}>
                    {e.campo}
                </Tag>
                <div style={{ marginTop: 6 }}>
                    <Codigo>{e.formula}</Codigo>
                </div>
                <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 6, marginBottom: 0 }}>
                    {e.explicacion}
                </Paragraph>
            </div>
        ))}
    </>
);

/**
 * Guía de fórmulas de Conceptos de Nómina.
 *
 * Panel lateral de sólo lectura. Se abre desde el listado de Conceptos y también
 * desde el campo Fórmula de cada pestaña de importe, para poder consultarla sin
 * cerrar el concepto que se está editando.
 */
const GuiaFormulas = ({ visible, onClose }) => (
    <Drawer
        title="Guía de fórmulas"
        placement="right"
        width={anchoDrawerGuia()}
        visible={visible}
        onClose={onClose}
        zIndex={Z_INDEX_GUIA}
        bodyStyle={{ paddingTop: 12 }}
    >
        <Title level={5} style={{ marginTop: 0 }}>
            Cómo se arman las fórmulas de un concepto
        </Title>
        <Tabs defaultActiveKey="empezar" size="small">
            <TabPane tab="Cómo empezar" key="empezar">
                <TabComoEmpezar />
            </TabPane>
            <TabPane tab="Los importes" key="importes">
                <TabImportes />
            </TabPane>
            <TabPane tab="Variables" key="variables">
                <TabVariables />
            </TabPane>
            <TabPane tab="Funciones" key="funciones">
                <TabFunciones />
            </TabPane>
            <TabPane tab="Ejemplos" key="ejemplos">
                <TabEjemplos />
            </TabPane>
        </Tabs>
    </Drawer>
);

export default GuiaFormulas;
